import { useState, useEffect, useCallback } from 'react';
import './css/coptic.css';
import { onNavClick } from './router.js';

// Serve WebP via <picture> when the source is a .png; PNG remains the fallback.
function PngPicture({ src, alt, loading = 'lazy', className }) {
  const isPng = typeof src === 'string' && /\.png$/i.test(src);
  if (!isPng) {
    return <img src={src} alt={alt} loading={loading} className={className} />;
  }
  const webp = src.replace(/\.png$/i, '.webp');
  return (
    <picture>
      <source srcSet={webp} type="image/webp" />
      <img src={src} alt={alt} loading={loading} className={className} />
    </picture>
  );
}

const LIGHTBOX_SHOTS = [
  { src: '/case-studies/coptic/screens/hero-phone.png', tag: 'Fig. 01 · Hymn player', alt: 'Hymn player preview' },
  { src: '/case-studies/coptic/foundation-thumb.svg', tag: 'Fig. A · Lineage', alt: 'Coptic foundation timeline' },
  { src: '/case-studies/coptic/screens/lessons.png', tag: 'Fig. B · Lessons', alt: 'Lessons screen showing rank progression' },
  { src: '/case-studies/coptic/screens/today.png', tag: 'Fig. 02 · Today', alt: 'Today screen, light and dark' },
  { src: '/case-studies/coptic/screens/hymns.png', tag: 'Fig. 03 · Hymns', alt: 'Hymn player, light and dark' },
  { src: '/case-studies/coptic/screens/practice.png', tag: 'Fig. 04 · Practice', alt: 'Practice screen, light and dark' },
  { src: '/case-studies/coptic/screens/quiz.png', tag: 'Fig. 05 · Quiz', alt: 'Quiz screen, light and dark' },
  { src: '/case-studies/coptic/screens/lectionary.png', tag: 'Fig. 06 · Lectionary', alt: 'Lectionary screen, light and dark' },
];

const RANKS = [
  {
    coptic: 'ⲯⲁⲗⲧⲱⲥ',
    english: 'Epsaltos',
    role: 'Chanter',
    duty: 'Leads and responds with hymns and congregational chants throughout the liturgy.',
  },
  {
    coptic: 'Ⲁⲛⲁⲅⲛⲱⲥⲧⲏⲥ',
    english: 'Anagnostis',
    role: 'Reader',
    duty: 'Reads the Epistles and Acts aloud to the congregation.',
  },
  {
    coptic: 'Ⲩⲡⲟⲇⲓⲁⲕⲟⲛⲟⲥ',
    english: 'Ypodiakon',
    role: 'Subdeacon',
    duty: 'Prepares and serves the altar, assisting with order and sacred vessels.',
  },
  {
    coptic: 'Ⲇⲓⲁⲕⲟⲛⲟⲥ',
    english: 'Diakon',
    role: 'Deacon',
    duty: 'Proclaims the Gospel and directs the liturgical responses.',
  },
  {
    coptic: 'Ⲁⲣⲭⲓⲇⲓⲁⲕⲟⲛⲟⲥ',
    english: 'Archidiakon',
    role: 'Archdeacon',
    duty: 'Oversees the service and coordinates the deacons in proper order.',
  },
];

const FEATURES = [
  {
    num: 'Feature 02',
    headline: 'Today, before the altar.',
    duty: 'For the deacon arriving at dawn.',
    image: '/case-studies/coptic/screens/today.png',
    tag: 'Fig. 02 · Today',
    bg: 'cream',
    body: 'A deacon shows up to liturgy with the day\'s hymns, responses, and readings already in his bones. The Today screen frames a single-tap commitment: today\'s tone, today\'s hymn, today\'s Gospel, and a quiet streak count that rewards habit, not vanity. Service requires preparation. The app turns preparation into a five-minute morning ritual.',
  },
  {
    num: 'Feature 03',
    headline: 'Three scripts, one breath.',
    duty: 'For the chanter still learning the Coptic alphabet.',
    image: '/case-studies/coptic/screens/hymns.png',
    tag: 'Fig. 03 · Hymns',
    bg: 'burgundy',
    reverse: true,
    body: 'Epsaltos and Anagnostis lead the congregation through dozens of hymns spanning five tones (Annual, Festive, Lenten, Sad and Pascha, Palm Sunday) and seasonal occasions. The hymn player shows three synchronized layers: the original Coptic script, a transliteration for those still learning the script, and a line-for-line English translation. Each layer can be toggled. Lyrics scroll line-by-line as the hymn plays, so a young chanter can sing along until the words stick.',
  },
  {
    num: 'Feature 04',
    headline: 'Rehearsal you can hear.',
    duty: 'For the deacon who cannot afford a wrong note in front of the people.',
    image: '/case-studies/coptic/screens/practice.png',
    tag: 'Fig. 04 · Practice',
    bg: 'ink',
    body: 'Deacons sing in front of a congregation. Sight-reading a hymn for the first time at the altar is unforgiving. Practice is a private rehearsal space: record yourself singing a hymn, play it back against the master recording, and self-correct without shame. Stacked waveforms and side-by-side timing make pronunciation gaps visible.',
  },
  {
    num: 'Feature 05',
    headline: 'Recall under pressure.',
    duty: 'For the deacon mid-litany who cannot reach for a phone.',
    image: '/case-studies/coptic/screens/quiz.png',
    tag: 'Fig. 05 · Quiz',
    bg: 'burgundy-2',
    reverse: true,
    body: 'In the middle of a litany a deacon cannot reach for a phone. He has to remember. Quiz mode tests him on response prompts, hymn-tone identification, and Coptic-to-English vocabulary across all five ranks. Sessions are short, spaced over weeks, and weighted toward the items most likely to come up in this week\'s actual services.',
  },
  {
    num: 'Feature 06',
    headline: 'Knowing the day.',
    duty: 'For the Reader who must prepare the right Epistle in the right tone.',
    image: '/case-studies/coptic/screens/lectionary.png',
    tag: 'Fig. 06 · Lectionary',
    bg: 'cream',
    body: 'Every day in the Coptic calendar carries its own readings: Synaxarium, Pauline, Catholic, Praxis, Psalm, and Gospel, plus a tone for that season. A new deacon often does not know which is which. Calendar and Lectionary surface today\'s liturgical context up front, so a Reader can prepare the right Epistle in the right tone without a spreadsheet of feast days.',
  },
];

const FACTS = [
  { k: 'AD 42', v: 'Founded by St. Mark in Alexandria.' },
  { k: 'AD 190', v: 'Catechetical School, oldest of its kind.' },
  { k: 'Pope Tawadros II', v: '118th in apostolic succession.' },
  { k: '~10M faithful', v: 'Across a global diaspora today.' },
];

export default function CopticCase({ data }) {
  const [lightbox, setLightbox] = useState(null);
  const open = useCallback((i) => setLightbox(i), []);
  const close = useCallback(() => setLightbox(null), []);
  const step = useCallback((dir) => {
    setLightbox((cur) => cur === null ? null : (cur + dir + LIGHTBOX_SHOTS.length) % LIGHTBOX_SHOTS.length);
  }, []);

  useEffect(() => {
    if (lightbox === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightbox, close, step]);

  return (
    <article className="coptic-case">
      <CCNav />
      <CCHero onShot={() => open(0)} />
      <CCFoundation onShot={() => open(1)} />
      <CCRanks onShot={() => open(2)} />
      {FEATURES.map((f, i) => <CCFeature key={i} {...f} onShot={() => open(3 + i)} />)}
      <CCClose />
      <CCNext next={data?.next} />
      {lightbox !== null && (
        <CCLightbox
          shot={LIGHTBOX_SHOTS[lightbox]}
          index={lightbox}
          total={LIGHTBOX_SHOTS.length}
          onClose={close}
          onPrev={() => step(-1)}
          onNext={() => step(1)}
        />
      )}
    </article>
  );
}

function CCNav() {
  return (
    <nav className="cc-nav">
      <a href="/" onClick={onNavClick} className="cc-nav__back">← Back to work</a>
      <span className="cc-nav__brand">Coptic Deacon Training</span>
      <div className="cc-nav__right">
        <span className="cc-nav__year">2026</span>
        <a href="/coptic/" className="cc-nav__try" target="_blank" rel="noopener noreferrer">Try the app</a>
      </div>
    </nav>
  );
}

function CCHero({ onShot }) {
  return (
    <section className="cc-hero">
      <div className="cc-container cc-hero__inner">
        <div>
          <div className="cc-mono cc-hero__eyebrow">Fractional · Designer & Developer · 2026</div>
          <h1 className="cc-hero__title">
            <span className="cc-hero__quotemark" aria-hidden="true">“</span>Let all things be done in the Church <em>decently and in order,</em> each according to the rank delivered to him.<span className="cc-hero__quotemark" aria-hidden="true">”</span>
          </h1>
          <div className="cc-hero__attrib">
            <span className="cc-hero__attrib-name">St. Athanasius the Apostolic</span>
            <span className="cc-hero__attrib-meta">Coptic Pope of Alexandria · 328 to 373 AD</span>
          </div>
          <p className="cc-hero__lede">
            A liturgy older than nations, learned in five minutes a day. Coptic deacons memorize hundreds of hymns, responses, readings, and tones across five ascending ranks. The tools available today are paper hymnals and YouTube playlists.
          </p>
          <p className="cc-hero__lede">
            This is a self-initiated prototype that turns daily preparation into a quiet ritual. It teaches Coptic script, plays the chant, drills the responses, and adapts to a seven-year-old chanter or a forty-year-old learning late.
          </p>
          <button type="button" className="cc-hero__cta" onClick={onShot}>
            See the screens <span>→</span>
          </button>
        </div>
        <div>
          <button type="button" className="cc-hero__phone cc-shot" onClick={onShot} aria-label="Open hymn player screenshot">
            <PngPicture src="/case-studies/coptic/screens/hero-phone.png" alt="Hymn player preview" />
            <span className="cc-hero__phone-tag">Fig. 01 · Hymn player</span>
            <span className="cc-shot__hint" aria-hidden="true">↗ Expand</span>
          </button>
        </div>
      </div>
      <img className="cc-hero__seal" src="/case-studies/coptic/coptic-cross.png" alt="Coptic cross" loading="lazy" />
    </section>
  );
}

function CCFoundation({ onShot }) {
  const intro = "The Coptic Orthodox Church was founded by Saint Mark the Evangelist in Alexandria around AD 42, less than a decade after the Ascension. It is among the oldest Christian institutions in the world, and the Catechetical School of Alexandria is considered the oldest Christian school of theology. Every deacon trained by this app inherits a liturgy, a chant tradition, and a service older than most nations.";
  return (
    <section className="cc-foundation">
      <div className="cc-container cc-foundation__grid">
        <div>
          <div className="cc-mono">Chapter 01 · Inheritance</div>
          <h2 className="cc-foundation__h">Among the oldest Christian traditions in the world.</h2>
          <p className="cc-foundation__body">{intro}</p>
          <div className="cc-facts">
            {FACTS.map((f, i) => (
              <div key={i} className="cc-fact">
                <div className="cc-fact__k">{f.k}</div>
                <div className="cc-fact__v">{f.v}</div>
              </div>
            ))}
          </div>
        </div>
        <button type="button" className="cc-foundation__art cc-shot" onClick={onShot} aria-label="Open lineage timeline">
          <span className="cc-mono cc-foundation__art-tag">Fig. A · Lineage</span>
          <img src="/case-studies/coptic/foundation-thumb.svg" alt="Coptic foundation timeline" loading="lazy" />
          <span className="cc-shot__hint" aria-hidden="true">↗ Expand</span>
        </button>
      </div>
    </section>
  );
}

function CCRanks({ onShot }) {
  return (
    <section className="cc-ranks">
      <div className="cc-container">
        <div className="cc-ranks__head">
          <div>
            <div className="cc-mono">Chapter 02 · The ladder</div>
            <h2 className="cc-ranks__h">
              <span className="cc-hero__quotemark" aria-hidden="true">“</span>The deacon is a <em>servant</em> in the house of God, not a rank for honor.<span className="cc-hero__quotemark" aria-hidden="true">”</span>
            </h2>
          </div>
          <div className="cc-ranks__attrib">
            <span className="cc-ranks__attrib-name">Pope Shenouda III</span>
            <span className="cc-ranks__attrib-meta">117th Coptic Pope of Alexandria · 1971 to 2012</span>
          </div>
        </div>
        <div className="cc-ranks__layout">
          <div className="cc-ladder">
            {RANKS.map((r, i) => (
              <div key={i} className="cc-rung">
                <div className="cc-rung__num">RANK 0{i + 1}</div>
                <div className="cc-rung__names">
                  <span className="cc-rung__coptic">{r.coptic}</span>
                  <span className="cc-rung__english">{r.english} · {r.role}</span>
                  <span className="cc-rung__duty">{r.duty}</span>
                </div>
              </div>
            ))}
          </div>
          <button type="button" className="cc-ranks__shot cc-shot" onClick={onShot} aria-label="Open lessons screenshot">
            <PngPicture src="/case-studies/coptic/screens/lessons.png" alt="Lessons screen showing rank progression" />
            <span className="cc-shot__hint" aria-hidden="true">↗ Expand</span>
          </button>
        </div>
      </div>
    </section>
  );
}

function CCFeature({ num, headline, duty, body, image, tag, bg, reverse, onShot }) {
  const cls = [
    'cc-feature',
    `cc-feature--${bg}`,
    reverse ? 'cc-feature--reverse' : '',
  ].filter(Boolean).join(' ');
  return (
    <section className={cls}>
      <div className="cc-container cc-feature__grid">
        <div className="cc-feature__copy">
          <div className="cc-mono cc-feature__num">{num}</div>
          <h2 className="cc-feature__h">{headline}</h2>
          <p className="cc-feature__duty">{duty}</p>
          <p className="cc-feature__body">{body}</p>
        </div>
        <button type="button" className="cc-feature__shot cc-shot" onClick={onShot} aria-label={`Open ${tag} screenshot`}>
          <span className="cc-feature__shot-tag">{tag}</span>
          <PngPicture src={image} alt={headline} />
          <span className="cc-shot__hint" aria-hidden="true">↗ Expand</span>
        </button>
      </div>
    </section>
  );
}

function CCClose() {
  return (
    <section className="cc-close">
      <div className="cc-container cc-close__inner">
        <div className="cc-close__eyebrow">An invitation</div>
        <h2 className="cc-close__h">Open the app.</h2>
        <p className="cc-close__sub">Five minutes a day. The chant, the script, the readings of the day. See how the prototype feels in your hand.</p>
        <div className="cc-close__buttons">
          <a href="/" onClick={onNavClick} className="cc-btn cc-btn--ghost">Back to work</a>
        </div>
        <div className="cc-close__fine">
          <span>Self-initiated · In active development · 2026</span>
          <span>Designed and built by Ryan Marfone</span>
          <span>Liturgy and ranks per the Coptic Orthodox tradition</span>
        </div>
      </div>
    </section>
  );
}

function CCNext({ next }) {
  if (!next) return null;
  return (
    <section className="cc-next">
      <div className="cc-container">
        <div className="cc-next__label">Next case</div>
        <a href={`/case/${next.slug}`} onClick={onNavClick} className="cc-next__link">
          {next.title}<span className="cc-next__arrow">→</span>
        </a>
      </div>
    </section>
  );
}

function CCLightbox({ shot, index, total, onClose, onPrev, onNext }) {
  return (
    <div className="cc-lightbox" role="dialog" aria-modal="true" aria-label={shot.alt}>
      <button type="button" className="cc-lightbox__backdrop" onClick={onClose} aria-label="Close" />
      <div className="cc-lightbox__chrome">
        <div className="cc-lightbox__top">
          <span className="cc-lightbox__tag">{shot.tag}</span>
          <span className="cc-lightbox__count">{String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
          <button type="button" className="cc-lightbox__close" onClick={onClose} aria-label="Close lightbox">×</button>
        </div>
        <div className="cc-lightbox__stage">
          <button type="button" className="cc-lightbox__nav cc-lightbox__nav--prev" onClick={onPrev} aria-label="Previous image">‹</button>
          {/\.png$/i.test(shot.src) ? (
            <picture key={shot.src}>
              <source srcSet={shot.src.replace(/\.png$/i, '.webp')} type="image/webp" />
              <img src={shot.src} alt={shot.alt} />
            </picture>
          ) : (
            <img src={shot.src} alt={shot.alt} key={shot.src} />
          )}
          <button type="button" className="cc-lightbox__nav cc-lightbox__nav--next" onClick={onNext} aria-label="Next image">›</button>
        </div>
        <div className="cc-lightbox__hint">
          <span>← / →</span><span>·</span><span>Esc to close</span>
        </div>
      </div>
    </div>
  );
}
